// RoleGate — sits inside PrivateRoute and routes the user to the right
// shell based on whether they own a business. Owners go to /portal (CRM),
// clients go to /app (client portal). Manual visits to the wrong tree
// are redirected here.
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useRole } from "./RoleContext";
import "../../pages/CRMDashboard.css";

export default function RoleGate({ require }) {
  const { role, loading } = useRole();
  const location = useLocation();

  // Show the wave-dot loader whenever role is unknown at the index gate
  // (no `require`). We CANNOT redirect away from "/" without infinite-looping,
  // so we wait here until role resolves. This also covers the brief render
  // window after sign-in where role=null and loading=false (RoleContext's
  // effect hasn't yet flipped loading back to true for the new user).
  if (loading || (!role && !require)) {
    const theme =
      (typeof window !== "undefined" && localStorage.getItem("polyp_theme")) ||
      "dark";
    return (
      <div className={`crm-loading ${theme === "light" ? "light-theme" : ""}`}>
        <div className="crm-wave-dots">
          <span className="crm-wave-dot" />
          <span className="crm-wave-dot" />
          <span className="crm-wave-dot" />
          <span className="crm-wave-dot" />
          <span className="crm-wave-dot" />
        </div>
      </div>
    );
  }

  if (!role) {
    // Gated route (require is set) with no role yet — bounce to "/" so the
    // index gate can decide once role resolves. The index gate will show
    // the loader (above) and then Navigate to /portal or /app.
    return <Navigate to="/" replace />;
  }

  const isOwner = role.isBusinessOwner;
  const isAdmin = role.isAdmin;
  // Instagram lead-ad parents who haven't been placed into an academy yet.
  // Only meaningful for non-owners/non-admins — an owner or admin flag wins.
  const isAwaitingPlacement =
    !isOwner && !isAdmin && role.placementStatus === "awaiting_placement";
  // Unified PW Manager (2026-09-24): League / Coach / Official are ONE
  // account. Every staff tier may use the scoring (/official) and team
  // (/coach) consoles — both scope everything to the caller server-side — and
  // the league console needs a league the account owns. Which console "/"
  // lands on still follows the legacy accountType, so nobody's landing moves.
  const STAFF = new Set(["official", "coach", "league", "manager"]);
  const isStaff = !isOwner && !isAdmin && STAFF.has(role.accountType);
  const canLeague = role.ownsLeague || role.accountType === "league";
  const staffHome = () => {
    switch (role.accountType) {
      case "official": return "/official";
      case "coach": return "/coach";
      case "league": return "/league";
      default: return canLeague ? "/league" : role.coachesTeam ? "/coach" : "/official";
    }
  };

  // Platform admins live entirely in the /admin tree. Send them there from any
  // non-admin gate; let them through only on admin-gated routes.
  if (isAdmin) {
    return require === "admin"
      ? <Outlet />
      : <Navigate to="/admin/officials" replace />;
  }

  // Non-admins can never reach admin routes.
  if (require === "admin") {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  if (isStaff) {
    if (require === "official" || require === "coach") return <Outlet />;
    if (require === "league") {
      return canLeague ? <Outlet /> : <Navigate to={staffHome()} replace />;
    }
    // The index gate, and any gate that isn't a staff console.
    return <Navigate to={staffHome()} replace />;
  }

  // REMOVED-CRM-ROUTES (2026-08-23) — the CRM is disabled: legacy accounts
  // (client parents, business owners, awaiting-placement leads) all land on
  // the standalone /retired page instead of the client portal / owner CRM /
  // finding-academy. The original redirects are preserved below — restore
  // them together with App.js's REMOVED-CRM-ROUTES route blocks.
  /*
  // Awaiting-placement parents live entirely on /finding-academy. Keep them out
  // of the normal client portal and the placement-gated route lets them in.
  if (require === "placement") {
    return isAwaitingPlacement
      ? <Outlet />
      : <Navigate to="/" replace state={{ from: location }} />;
  }
  if (isAwaitingPlacement) {
    return <Navigate to="/finding-academy" replace />;
  }

  if (require === "owner" && !isOwner) {
    return <Navigate to="/app" replace state={{ from: location }} />;
  }
  if (require === "client" && isOwner) {
    return <Navigate to="/portal" replace state={{ from: location }} />;
  }

  // No requirement — used as a top-level redirect at "/"
  if (!require) {
    return <Navigate to={isOwner ? "/portal" : "/app"} replace />;
  }

  return <Outlet />;
  */
  // END-REMOVED-CRM-ROUTES

  // Everyone left (client / owner / awaiting-placement — and any stale
  // "placement"/"owner"/"client"-gated URL) goes to the retired-account page.
  return <Navigate to="/retired" replace state={{ from: location, awaitingPlacement: isAwaitingPlacement }} />;
}
